import { useCallback, useMemo } from 'react';
import { logger } from '../../../../utils/logger';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';

/**
 * Hook to validate task status values against database constraints
 * This helps prevent task_status_check constraint violations
 */
export function useValidTaskStatus(topicId: string) {
  // Get columns using React Query
  const { data: columns = [], isLoading } = useTaskColumns(topicId);

  // Extract valid statuses from columns
  const validStatuses = useMemo(() => {
    const statuses = columns.map(col => col.status);

    if (statuses.length === 0) {
      logger.component('No task columns found, using default statuses', { topicId });
      // Default to standard statuses if we don't find any
      return ['todo', 'doing', 'done'];
    }

    return statuses;
  }, [columns, topicId]);

  // Function to check if a status is valid
  const isValidStatus = useCallback(
    (status: string) => {
      return validStatuses.includes(status);
    },
    [validStatuses]
  );

  // Function to get a safe status (fallback to 'todo' if invalid)
  const getSafeStatus = useCallback(
    (status: string) => {
      if (validStatuses.includes(status)) {
        return status;
      }

      // Log the invalid status
      logger.error('Invalid task status detected', {
        providedStatus: status,
        validOptions: validStatuses,
        usingFallback: true,
      });

      // Return a safe default
      return validStatuses.length > 0 ? validStatuses[0] : 'todo';
    },
    [validStatuses]
  );

  return {
    validStatuses,
    isValidStatus,
    getSafeStatus,
    isLoading,
  };
}
